// Chat state components for different UI states
interface ChatErrorStateProps {
  error?: string;
  errorDetails?: {
    title?: string;
    detail?: string;
    traceId?: string;
    instance?: string;
  };
  firstMessage?: string;
  onRetry?: () => void;
  onClearError?: () => void;
  isRetrying?: boolean;
}

export const ChatErrorState = ({ 
  error, 
  errorDetails,
  firstMessage, 
  onRetry, 
  onClearError,
  isRetrying = false 
}: ChatErrorStateProps) => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-red-600 dark:text-red-400 text-center max-w-2xl mx-auto p-6">
      {/* Main error title */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">
          {errorDetails?.title ?? "Generation Failed"}
        </h2>
        <p className="text-base text-red-500 dark:text-red-300">
          {errorDetails?.detail ?? error ?? "An unexpected error occurred"}
        </p>
      </div>

      {/* User's original message */}
      {firstMessage && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your original prompt:</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 italic">"{firstMessage}"</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 justify-center flex-wrap">
        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRetrying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Retrying...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </>
            )}
          </button>
        )}
        
        {onClearError && (
          <button
            onClick={onClearError}
            disabled={isRetrying}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Return to Chat
          </button>
        )}
      </div>
    </div>
  </div>
);

export const ChatLoadingState = () => (
  <div className="flex-1 flex items-center justify-center text-center">
    <div className="flex items-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      <span className="ml-2 text-gray-600 dark:text-gray-300">Loading messages...</span>
    </div>
  </div>
);

export const ChatEmptyState = ({ chatId }: { chatId: number | null }) => (
  <div className="flex-1 flex items-center justify-center text-center">
    <h1 className="text-4xl font-normal text-gray-900 dark:text-gray-100">
      {chatId ? 'No messages yet' : 'Welcome'}
    </h1>
  </div>
);
